import { useEffect, useRef } from "react";

/**
 * Hook that provides tools to manage request cancellation
 *
 * @returns Object with functions to control request cancellation
 */
export function useCancellableRequest(): {
  cancelRequest: () => void;
  getSignal: (cancelPrevious?: boolean) => AbortSignal;
  abortControllerRef: React.RefObject<AbortController | null>;
} {
  const abortControllerRef = useRef<AbortController | null>(null);

  // This effect handles cleanup when the component unmounts
  useEffect(() => {
    return (): void => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Cancels the current request if one exists
   */
  const cancelRequest = (): void => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  /**
   * Creates a new abort signal for a request, cancelling any previous request
   *
   * @param cancelPrevious Whether to cancel previous requests (default: true)
   * @returns AbortSignal to pass to fetch/axios
   */
  const getSignal = (cancelPrevious = true): AbortSignal => {
    if (cancelPrevious) {
      cancelRequest();
    }

    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  return {
    /** Cancels the current request if one exists */
    cancelRequest,
    /** Creates a new abort signal for a request */
    getSignal,
    /** Current abort controller reference (be careful with this) */
    abortControllerRef,
  };
}
